/**
 * VideoPlayerModal Component
 * 
 * Modal video player with YouTube/Vimeo support
 * 
 * Version: 6.2.0
 */
import React, { useEffect, useCallback } from 'react'
import { Tutorial } from '../data/tutorials'
import './VideoPlayerModal.css'

interface VideoPlayerModalProps {
  tutorial: Tutorial | null
  isOpen: boolean
  onClose: () => void
  onComplete?: (tutorial: Tutorial) => void
  className?: string
}

const VideoPlayerModal: React.FC<VideoPlayerModalProps> = ({
  tutorial,
  isOpen,
  onClose,
  onComplete,
  className = ''
}) => {
  // Handle escape key
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }, [onClose])

  // Add escape listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, handleEscape])

  // Get video embed URL
  const getEmbedUrl = (video: Tutorial['video']): string => {
    if (video.type === 'youtube') {
      const videoId = extractYouTubeId(video.url)
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`
    }
    if (video.type === 'vimeo') {
      const videoId = extractVimeoId(video.url)
      return `https://player.vimeo.com/video/${videoId}?autoplay=1`
    }
    return video.url
  }

  // Extract YouTube ID
  const extractYouTubeId = (url: string): string => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/
    const match = url.match(regExp)
    return (match && match[7].length === 11) ? match[7] : ''
  }

  // Extract Vimeo ID
  const extractVimeoId = (url: string): string => {
    const regExp = /vimeo.com\/([0-9]+)/
    const match = url.match(regExp)
    return match ? match[1] : ''
  }

  // Handle complete
  const handleEnded = () => {
    if (tutorial) {
      onComplete?.(tutorial)
    }
  }

  if (!isOpen || !tutorial) return null

  return (
    <div className={`video-player-modal ${className}`}>
      {/* Backdrop */}
      <div className="modal-backdrop" onClick={onClose} />
      
      {/* Modal Content */}
      <div className="modal-container">
        {/* Header */}
        <div className="modal-header">
          <div className="modal-info">
            <span className="modal-category">{tutorial.category.replace('-', ' ')}</span>
            <h2 className="modal-title">{tutorial.title}</h2>
          </div>
          <button className="modal-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Video Player */}
        <div className="video-container">
          {tutorial.video.type === 'youtube' && (
            <iframe
              src={getEmbedUrl(tutorial.video)}
              title={tutorial.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
          {tutorial.video.type === 'vimeo' && (
            <iframe
              src={getEmbedUrl(tutorial.video)}
              title={tutorial.title}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          )}
          {tutorial.video.type === 'local' && (
            <video
              src={tutorial.video.url}
              controls
              autoPlay
              onEnded={handleEnded}
            />
          )}
          {tutorial.video.type === 'external' && (
            <div className="external-video">
              <a href={tutorial.video.url} target="_blank" rel="noopener noreferrer">
                Open Video
              </a>
            </div>
          )}
        </div>

        {/* Description */}
        <div className="modal-description">
          <p>{tutorial.description}</p>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="modal-meta">
            <span className="meta-item">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
              </svg>
              {tutorial.duration}
            </span>
            <span className="meta-item">
              <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              {tutorial.difficulty}
            </span>
          </div>
          <div className="modal-tags">
            {tutorial.tags.map(tag => (
              <span key={tag} className="modal-tag">#{tag}</span>
            ))}
          </div>
        </div>

        {/* Related Tutorials Placeholder */}
        {tutorial.relatedTutorials && tutorial.relatedTutorials.length > 0 && (
          <div className="related-section">
            <h4>Related Tutorials</h4>
            <div className="related-list">
              {/* Related tutorials would be rendered here */}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default VideoPlayerModal